import { useState, useCallback, useEffect, useRef } from "react";
import { AxiosError } from "axios";
// import apiClient from "../config/api";
import type {
  User,
  UserAccountResponse,
  PasswordResetResponse,
  CreateAccountData,
  ChangePasswordData,
  LoginResponse,
} from "../types";
import { useGeneralApiCall } from "@/services/useGeneralApiCall";

interface UseUserManagementReturn {
  users: User[];
  isLoading: boolean;
  error: string | null;
  fetchUsers: () => Promise<void>;
  createAccount: (
    accountData: CreateAccountData
  ) => Promise<UserAccountResponse | null>;
  resetPassword: (userId: string) => Promise<PasswordResetResponse | null>;
  changeSystemPassword: (passwordData: ChangePasswordData) => Promise<boolean>;
  login: (email: string, password: string) => Promise<LoginResponse | null>;
  refreshUsers: () => void;
}

/**
 * Custom hook for user management operations following SOLID principles
 * Single Responsibility: Manages user-related operations
 * Open/Closed: Extensible for additional user operations
 * Dependency Inversion: Depends on apiClient abstraction
 */
export const useUserManagement = (): UseUserManagementReturn => {
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Use ref to avoid dependency issues
  const fetchUsersRef = useRef<() => Promise<void>>(async () => {});

  const { getApi, postApi } = useGeneralApiCall();

  // Fetch all staff users
  const fetchUsers = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      //   const response = await apiClient.get<User[]>("/auth/v1/staff/list/");
      const response = await getApi<User[]>("/api/auth/v1/staff/list/");
      const fetchedUsers = response.data || [];

      // Validate and filter users
      //   const validUsers = fetchedUsers.filter(
      //     (user): user is User =>
      //       user &&
      //       typeof user === "object" &&
      //       "user_id" in user &&
      //       "email" in user &&
      //       "name" in user &&
      //       Boolean(user.user_id) &&
      //       Boolean(user.email) &&
      //       Boolean(user.name)
      //   );

      setUsers(fetchedUsers);
    } catch (err) {
      const errorMessage =
        err instanceof AxiosError
          ? err.response?.data?.message || "Failed to fetch users"
          : "An unexpected error occurred";
      setError(errorMessage);
      console.error("Error fetching users:", err);
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Update ref when fetchUsers changes
  useEffect(() => {
    fetchUsersRef.current = fetchUsers;
  }, [fetchUsers]);

  // Create new user account
  const createAccount = useCallback(
    async (
      accountData: CreateAccountData
    ): Promise<UserAccountResponse | null> => {
      try {
        setError(null);

        // const response = await apiClient.post("/auth/v1/create-account/", {
        //   name: accountData.name,
        //   email: accountData.email,
        //   ...(accountData.role && { role: accountData.role }),
        // });

        const response = await postApi<any>("/auth/v1/create-account/", {
          name: accountData.name,
          email: accountData.email,
          ...(accountData.role && { role: accountData.role }),
        });

        console.log("Create account full response:", response);
        console.log("Create account response.data:", response.data);

        // Handle Django API response - check multiple possible structures
        let accountInfo = null;

        // Check if data is nested under response.data.data
        if (response.data && response.data.data) {
          accountInfo = response.data.data;
        }
        // Check if data is directly in response.data
        else if (response.data && response.data.name && response.data.email) {
          accountInfo = response.data;
        }

        // Map Django response fields to our expected structure
        if (
          accountInfo &&
          accountInfo.name &&
          accountInfo.email &&
          accountInfo.system_generated_password
        ) {
          const mappedResponse: UserAccountResponse = {
            user_id: accountInfo.email, // Use email as ID since we don't get a separate ID
            user_name: accountInfo.name,
            user_email: accountInfo.email,
            password: accountInfo.system_generated_password,
          };

          console.log("Mapped response:", mappedResponse);

          // Refresh users list to include new user
          await fetchUsersRef.current();
          return mappedResponse;
        }

        // If we reach here, the response structure was unexpected
        console.warn("Unexpected response structure:", response.data);
        setError(
          "Account may have been created but response format was unexpected. Please refresh the page."
        );
        await fetchUsersRef.current(); // Still refresh users in case it was created
        return null;
      } catch (err) {
        const errorMessage =
          err instanceof AxiosError
            ? err.response?.data?.message || "Failed to create account"
            : "An unexpected error occurred";
        setError(errorMessage);
        console.error("Error creating account:", err);
        return null;
      }
    },
    []
  ); // Remove fetchUsers dependency

  // Reset user password
  const resetPassword = useCallback(
    async (userId: string): Promise<PasswordResetResponse | null> => {
      try {
        setError(null);

        // const response = await apiClient.post<PasswordResetResponse>(
        //   `/auth/v1/admin-reset-password/${userId}/`
        // );

        const response = await postApi<PasswordResetResponse>(
          `/auth/v1/admin-reset-password/${userId}/`
        );

        const resetData = response.data;
        if (
          resetData &&
          resetData.user_id &&
          resetData.user_email &&
          resetData.password
        ) {
          return resetData;
        }
        return null;
      } catch (err) {
        const errorMessage =
          err instanceof AxiosError
            ? err.response?.data?.message || "Failed to reset password"
            : "An unexpected error occurred";
        setError(errorMessage);
        console.error("Error resetting password:", err);
        return null;
      }
    },
    []
  );

  // Change system password (for first-time login)
  const changeSystemPassword = useCallback(
    async (passwordData: ChangePasswordData): Promise<boolean> => {
      try {
        setError(null);

        // Validate password confirmation
        if (passwordData.new_password !== passwordData.confirm_password) {
          setError("New password and confirmation do not match");
          return false;
        }

        // await apiClient.post("/auth/v1/change-system-password/", {
        //   email: passwordData.email,
        //   old_password: passwordData.old_password,
        //   new_password: passwordData.new_password,
        //   confirm_password: passwordData.confirm_password,
        // });

        await postApi("/auth/v1/change-system-password/", {
          email: passwordData.email,
          old_password: passwordData.old_password,
          new_password: passwordData.new_password,
          confirm_password: passwordData.confirm_password,
        });

        return true;
      } catch (err) {
        const errorMessage =
          err instanceof AxiosError
            ? err.response?.data?.message || "Failed to change password"
            : "An unexpected error occurred";
        setError(errorMessage);
        console.error("Error changing password:", err);
        return false;
      }
    },
    []
  );

  // User login
  const login = useCallback(
    async (email: string, password: string): Promise<LoginResponse | null> => {
      try {
        setError(null);

        // const response = await apiClient.post<LoginResponse>(
        //   "/auth/v1/auth/login/",
        //   {
        //     email,
        //     password,
        //   }
        // );

        const response = await postApi<LoginResponse>("/auth/v1/auth/login/", {
          email,
          password,
        });

        const loginData = response.data;
        if (
          loginData &&
          loginData.tokens &&
          loginData.tokens.access &&
          loginData.tokens.refresh
        ) {
          return loginData;
        }
        return null;
      } catch (err) {
        const errorMessage =
          err instanceof AxiosError
            ? err.response?.data?.message || "Login failed"
            : "An unexpected error occurred";
        setError(errorMessage);
        console.error("Error during login:", err);
        return null;
      }
    },
    []
  );

  // Refresh users (alias for fetchUsers)
  const refreshUsers = useCallback(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Initial fetch
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  return {
    users,
    isLoading,
    error,
    fetchUsers,
    createAccount,
    resetPassword,
    changeSystemPassword,
    login,
    refreshUsers,
  };
};
